import DOMPurify from "isomorphic-dompurify";
import { stripUrl } from "@/lib/excerptImageGenerator";
import type { OverlayConfig } from "./OverlayPanel";

type ExcerptImagePreviewProps = {
  previewRef: React.RefObject<HTMLDivElement>;
  backgroundCss: string;
  textColor: string;
  fontStack: string;
  dividerColor: string;
  bgMode: "light" | "dark" | "custom" | "brand" | "gradient";
  overlay: OverlayConfig;
  bodyHtml: string;
  authorName: string;
  pubTitle: string;
  displayUrl: string;
  logoRenderUrl: string | null;
  isDefaultCornerMark: boolean;
  decoOpacity: number;
  logoOpacity: number;
};

/**
 * The 1200×1200 export canvas (rendered offscreen at the source size and
 * scaled by the parent). Extracted from ExcerptImageTab to keep that file
 * focused on state + side-effects.
 */
export const ExcerptImagePreview = ({
  previewRef,
  backgroundCss,
  textColor,
  fontStack,
  dividerColor,
  bgMode,
  overlay,
  bodyHtml,
  authorName,
  pubTitle,
  displayUrl,
  logoRenderUrl,
  isDefaultCornerMark,
  decoOpacity,
  logoOpacity,
}: ExcerptImagePreviewProps) => {
  return (
    <div
      ref={previewRef}
      style={{
        width: 1200,
        background: backgroundCss,
        color: textColor,
        fontFamily: fontStack,
        padding: "96px 96px 72px 96px",
        minHeight: 1200,
        maxHeight: 1800,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <style>{`.excerptus-quote-body, .excerptus-quote-body * { color: ${textColor} !important; border-color: ${dividerColor} !important; }`}</style>
      {overlay.enabled && (bgMode === "gradient") && (
        <>
          {overlay.blur > 0 && (
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: -overlay.blur * 2,
                background: backgroundCss,
                filter: `blur(${overlay.blur}px)`,
                zIndex: 0,
              }}
            />
          )}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: overlay.color,
              opacity: overlay.opacity,
              zIndex: 1,
            }}
          />
        </>
      )}
      <div className="excerptus-quote-body" style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", color: textColor }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative" }}>
          <div
            style={{ fontSize: 60, lineHeight: 1.35, fontWeight: 500, position: "relative" }}
            dangerouslySetInnerHTML={{
              __html: (() => {
                const safeBody = DOMPurify.sanitize(bodyHtml);
                const openQuote = `<span style="position:absolute;right:100%;margin-right:12px;opacity:${decoOpacity};">“</span>`;
                const closeQuote = `<span style="opacity:${decoOpacity};margin-left:8px;">”</span>`;
                const withClose = /(<\/(p|h[1-6]|li|blockquote|div)>)\s*$/i.test(safeBody)
                  ? safeBody.replace(/(<\/(p|h[1-6]|li|blockquote|div)>)\s*$/i, `${closeQuote}$1`)
                  : safeBody + closeQuote;
                return openQuote + withClose;
              })(),
            }}
          />
        </div>
        <div style={{ marginTop: 64 }}>
          {authorName && <div style={{ fontSize: 40, fontWeight: 700 }}>{authorName}</div>}
          {pubTitle && <div style={{ fontSize: 32, opacity: 0.85, marginTop: 6 }}>{pubTitle}</div>}
        </div>
        <div style={{ borderTop: `1px solid ${dividerColor}`, marginTop: 40, paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontFamily: "Inter, system-ui, sans-serif", fontSize: 24, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", opacity: 0.85 }}>
              {stripUrl(displayUrl)}
            </div>
          </div>
          {logoRenderUrl && (
            <img
              src={logoRenderUrl}
              alt={isDefaultCornerMark ? "Excerptus corner mark" : "Custom logo"}
              style={{ height: 80, width: "auto", maxWidth: 320, objectFit: "contain", opacity: logoOpacity }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
